import { useCallback, useState } from 'react';
import { type IChatAttachment } from '@kognitos/lattice';
import { getAttachmentTypeFromFile } from '@/modules/file-renderer/utils/file-type-detection';

/**
 * Generates a unique ID for attachments.
 * Uses crypto.randomUUID() when available, falls back to timestamp + random.
 *
 * @returns {string} A unique attachment ID
 */
function generateAttachmentId(): string {
  // Use crypto.randomUUID() for better collision resistance (available in modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `attachment-${crypto.randomUUID()}`;
  }

  // Fallback for older browsers: timestamp + random number
  return `attachment-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Return type for useQuillChatAttachments hook.
 */
export interface IUseQuillChatAttachmentsReturn {
  /** Array of chat attachments */
  attachments: IChatAttachment[];
  /** Map of attachment IDs to File objects for upload */
  fileObjects: Map<string, File>;
  /** Handler for adding files (used with ChatInput onAddAttachments) */
  handleFilesAdd: (files: File[]) => void;
  /** Handler for removing an attachment */
  handleAttachmentRemove: (attachmentId: string) => void;
  /** Clears all attachments */
  clearAttachments: () => void;
  /**
   * Re-injects a previously cleared set of attachments (e.g. after a failed
   * create), keeping their ids so the retained File objects still match.
   */
  restoreAttachments: (
    attachments: IChatAttachment[],
    fileObjects: Map<string, File>,
  ) => void;
}

/**
 * Custom hook for managing file attachments in QuillChat.
 *
 * Handles file selection, conversion to chat attachment format,
 * and attachment state management. Stores file objects separately
 * for later upload operations.
 *
 * @returns {IUseQuillChatAttachmentsReturn} Attachment state and handlers
 *
 * @example
 * ```tsx
 * const {
 *   attachments,
 *   fileObjects,
 *   handleFilesAdd,
 *   handleAttachmentRemove,
 *   clearAttachments,
 * } = useQuillChatAttachments();
 *
 * <ChatInput
 *   attachments={attachments}
 *   onAddAttachments={handleFilesAdd}
 *   onAttachmentRemove={handleAttachmentRemove}
 * />
 * ```
 */
export function useQuillChatAttachments(): IUseQuillChatAttachmentsReturn {
  const [attachments, setAttachments] = useState<IChatAttachment[]>([]);
  const [fileObjects, setFileObjects] = useState<Map<string, File>>(new Map());

  const handleFilesAdd = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const newFileMap = new Map(fileObjects);
      const newAttachments: IChatAttachment[] = files.map(file => {
        const id = generateAttachmentId();
        newFileMap.set(id, file);

        return {
          id,
          name: file.name,
          type: getAttachmentTypeFromFile(file),
          size: file.size,
        };
      });

      setFileObjects(newFileMap);
      setAttachments(prev => [...prev, ...newAttachments]);
    },
    [fileObjects],
  );

  /**
   * Handles removing an attachment by ID.
   *
   * Removes both the attachment from the display list and the
   * corresponding file object from storage.
   *
   * @param {string} attachmentId - ID of the attachment to remove
   */
  const handleAttachmentRemove = useCallback((attachmentId: string) => {
    setAttachments(prev => prev.filter(att => att.id !== attachmentId));
    setFileObjects(prev => {
      const newMap = new Map(prev);
      newMap.delete(attachmentId);
      return newMap;
    });
  }, []);

  /**
   * Clears all attachments and file objects.
   */
  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setFileObjects(new Map());
  }, []);

  /**
   * Puts a cleared set of attachments back, unchanged.
   *
   * @param {IChatAttachment[]} restored - Attachments to display again
   * @param {Map<string, File>} restoredFiles - Their retained File objects
   */
  const restoreAttachments = useCallback(
    (restored: IChatAttachment[], restoredFiles: Map<string, File>) => {
      if (restored.length === 0) return;
      setAttachments(restored);
      setFileObjects(new Map(restoredFiles));
    },
    [],
  );

  return {
    attachments,
    fileObjects,
    handleFilesAdd,
    handleAttachmentRemove,
    clearAttachments,
    restoreAttachments,
  };
}
